import React, { useState } from 'react';
import './ConfirmEditPumpSchedule.scss';
import Modal from 'react-bootstrap/Modal';
import ModalHeaderTitle from '../../../AddLoads/ModalHeaderTitle';
import { CustomButton, Loading } from '../../../_common';
import ConfirmModalItem from '../../../_common/ConfirmModalItem/ConfirmModalItem';
import { useStoreContext } from '../../../App/StoreProvider';
import { WellheadInput, PumpScheduleSummaryObject } from '../../../../store/types/PumpScheduleEditState';
import {
    EditInfo,
    EditType,
    WellheadInfo,
    ProppantAddOrDeletion,
} from '../../../../interfaces/JobProfile/ConfirmModalInterfaces';
import _ from 'lodash';

const wellheadEditMap = new Map<string, any>();

const changedAttributes = (
    wellheadId: string,
    wellheadName: string,
    attribute: string,
    editType: EditType,
    oldValue: any,
    newValue: string | number,
): void => {
    const newWellheadEdit: EditInfo = {
        attributeName: attribute,
        oldValue: oldValue,
        newValue: newValue,
        editType: editType,
    };
    if (wellheadEditMap.has(wellheadId)) {
        wellheadEditMap.get(wellheadId).edits.push(newWellheadEdit);
    } else {
        const newWellheadInfo: WellheadInfo = {
            wellheadId: wellheadId,
            title: wellheadName,
            edits: Array(newWellheadEdit),
            addOrDeletions: [],
        };
        wellheadEditMap.set(wellheadId, newWellheadInfo);
    }
};

const deletedOrAddedProppants = (
    wellheadId: string,
    wellheadName: string,
    proppant: string,
    volume: number | null,
    plannedVolPerBox: number | null,
    editType: EditType,
): void => {
    const newWellheadEdit: ProppantAddOrDeletion = {
        proppant: proppant,
        volume: volume,
        plannedVolPerBox: plannedVolPerBox,
        editType: editType,
    };
    if (wellheadEditMap.has(wellheadId)) {
        wellheadEditMap.get(wellheadId).addOrDeletions.push(newWellheadEdit);
    } else {
        const newWellheadInfo: WellheadInfo = {
            wellheadId: wellheadId,
            title: wellheadName,
            edits: [],
            addOrDeletions: Array(newWellheadEdit),
        };
        wellheadEditMap.set(wellheadId, newWellheadInfo);
    }
};
const compareWellheadProppants = (
    newWellhead: WellheadInput,
    oldWellhead: PumpScheduleSummaryObject | undefined,
): void => {
    const newProppants = newWellhead.proppants;
    newProppants.map(newProppant => {
        if (newProppant['isDeleted']) {
            if (oldWellhead && oldWellhead.proppants) {
                if (oldWellhead.proppants.find(element => (element ? element.id == newProppant.id : false))) {
                    deletedOrAddedProppants(
                        newWellhead.id,
                        newWellhead.wellName,
                        newProppant.proppantName,
                        newProppant.totalVolume,
                        newProppant.volumePerBox,
                        EditType.PROPPANT_DELETE,
                    );
                }
            }
        } else if (newProppant['pumpScheduleId'] === null && newProppant['isDeleted'] === false) {
            deletedOrAddedProppants(
                newWellhead.id,
                newWellhead.wellName,
                newProppant.proppantName,
                newProppant.totalVolume,
                newProppant.volumePerBox,
                EditType.PROPPANT_ADD,
            );
        } else if (oldWellhead !== undefined) {
            const oldProppants = oldWellhead.proppants;
            if (oldProppants === undefined || oldProppants === null) return;
            const oldWellheadProppant = oldProppants.find(proppant => {
                if (proppant === undefined || proppant === null) return false;
                return proppant.id === newProppant.id;
            }) as any;
            Object.entries(newProppant).forEach(([newProppantKey, newProppantValue]) => {
                if (
                    oldWellheadProppant === undefined ||
                    oldWellheadProppant === null ||
                    newProppantKey === 'proppantId'
                )
                    return;
                const oldProppantValue = oldWellheadProppant[newProppantKey];
                if (oldProppantValue !== newProppantValue) {
                    changedAttributes(
                        newWellhead.id,
                        newWellhead.wellName,
                        newProppantKey,
                        EditType.PROPPANT_INFO,
                        oldProppantValue,
                        newProppantValue,
                    );
                }
            });
        }
    });
};

const preparePumpScheduleEdits = (
    pumpScheduleSummaryList: PumpScheduleSummaryObject[],
    toBePosted: WellheadInput[],
): void => {
    toBePosted.map(newWellhead => {
        const oldWellhead = pumpScheduleSummaryList.find(
            oldWellhead => oldWellhead.id === newWellhead.id || oldWellhead.wellName === newWellhead.wellName,
        );
        if (oldWellhead === undefined) {
            const newProppants = newWellhead.proppants;
            newProppants
                .filter(p => {
                    return p.isDeleted && p.pumpScheduleId == null ? false : true;
                })
                .forEach(proppant =>
                    deletedOrAddedProppants(
                        newWellhead.id,
                        newWellhead.wellName,
                        proppant.proppantName,
                        proppant.totalVolume,
                        proppant.volumePerBox,
                        EditType.PROPPANT_ADD,
                    ),
                );
        }
        Object.entries(newWellhead).forEach(([newWellheadKey, newWellheadValue]) => {
            if (oldWellhead === undefined || oldWellhead === null) return;
            const oldWellheadValue = oldWellhead[newWellheadKey];
            if (newWellheadKey === 'proppants') {
                compareWellheadProppants(newWellhead, oldWellhead);
                return;
            }
            if (oldWellheadValue !== newWellheadValue) {
                changedAttributes(
                    newWellhead.id,
                    newWellhead.wellName,
                    newWellheadKey,
                    EditType.WELLHEAD_INFO,
                    oldWellheadValue,
                    newWellheadValue,
                );
            }
        });
    });
};

const getEditComponents = (wellheadEditMap: Map<string, any>): React.ReactElement[] => {
    const components: React.ReactElement[] = [];
    let index = 0;

    wellheadEditMap.forEach((value, key) => {
        const editObject = value as WellheadInfo;
        if (editObject !== undefined && editObject.edits !== undefined) {
            const wellheadInfoEdits = editObject.edits.filter(edit => edit.editType === EditType.WELLHEAD_INFO);
            const proppantInfoEdits = editObject.edits.filter(edit => edit.editType === EditType.PROPPANT_INFO);

            if (wellheadInfoEdits.length > 0) {
                index = index + 1;
                components.push(
                    <ConfirmModalItem
                        index={index}
                        key={key + index}
                        title={editObject.title}
                        editType={EditType.WELLHEAD_INFO}
                        edits={editObject.edits.filter(edit => edit.editType === EditType.WELLHEAD_INFO)}
                    ></ConfirmModalItem>,
                );
            }

            if (proppantInfoEdits.length > 0) {
                index = index + 1;
                components.push(
                    <ConfirmModalItem
                        index={index}
                        key={key + index}
                        title={editObject.title}
                        editType={EditType.PROPPANT_INFO}
                        edits={editObject.edits.filter(edit => edit.editType === EditType.PROPPANT_INFO)}
                    ></ConfirmModalItem>,
                );
            }
        }

        if (editObject !== undefined && editObject.addOrDeletions !== undefined) {
            const proppantInfoDelete = editObject.addOrDeletions.filter(
                edit => edit.editType === EditType.PROPPANT_DELETE,
            );
            const proppantInfoAdd = editObject.addOrDeletions.filter(edit => edit.editType === EditType.PROPPANT_ADD);
            if (proppantInfoDelete.length > 0) {
                index = index + 1;
                components.push(
                    <ConfirmModalItem
                        index={index}
                        key={key + index}
                        title={editObject.title}
                        editType={EditType.PROPPANT_DELETE}
                        proppantAddOrDeletions={proppantInfoDelete.filter(
                            edit => edit.editType === EditType.PROPPANT_DELETE,
                        )}
                    ></ConfirmModalItem>,
                );
            }

            if (proppantInfoAdd.length > 0) {
                index = index + 1;
                components.push(
                    <ConfirmModalItem
                        index={index}
                        key={key + index}
                        title={editObject.title}
                        editType={EditType.PROPPANT_ADD}
                        proppantAddOrDeletions={proppantInfoAdd.filter(edit => edit.editType === EditType.PROPPANT_ADD)}
                    ></ConfirmModalItem>,
                );
            }
        }
    });
    return components;
};

interface ConfirmEditPumpScheduleProps {
    pumpScheduleSummaryList: PumpScheduleSummaryObject[];
    onEditSave: (onSaveComplete: () => void) => void;
}

const ConfirmEditPumpSchedule = ({
    onEditSave,
    pumpScheduleSummaryList,
}: ConfirmEditPumpScheduleProps): React.ReactElement => {
    const [
        {
            pumpScheduleEdit: { toBePosted, fieldErrors },
        },
    ] = useStoreContext();

    const [show, setShow] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const handleClose = (): void => {
        wellheadEditMap.clear();
        setShow(false);
    };

    const handleContinue = (): void => {
        setIsLoading(true);
        onEditSave(() => {
            // Save is complete
            wellheadEditMap.clear();
            setShow(false);
            setIsLoading(false);
            setIsSaveDisabled(false);
        });
    };

    const isFormValid = (): boolean => {
        let isValid = true;
        for (const key in fieldErrors) {
            if (!!fieldErrors[key] && Object.values(fieldErrors[key]).length > 0) {
                for (const value of Object.values(fieldErrors[key])) {
                    if (value !== undefined && value != '') {
                        isValid = false;
                    }
                }
            }
        }
        return isValid;
    };

    const isPositiveNumber = (v: any): boolean => {
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) && n > 0;
    };

    const isDisabled = (): boolean => {
        let pageDisabled = false;
        if (toBePosted && toBePosted !== undefined && toBePosted.length > 0 && isFormValid()) {
            toBePosted.map((item: any) => {
                if (item) {
                    let rowError = false;
                    if (item.isDeleted !== undefined && item.isDeleted) rowError = false;

                    if (item.isDeleted) {
                        // Skip required-fields and proppants validation for deleted wellheads
                    } else if (
                        isPositiveNumber(item.stageTime) &&
                        isPositiveNumber(item.stages) &&
                        !!item.wellName &&
                        !!item.proppants
                    ) {
                        item.proppants.map((proppant: any) => {
                            if (proppant) {
                                const rowIsDeleted = proppant.isDeleted !== undefined && proppant.isDeleted;
                                if (!rowIsDeleted) {
                                    rowError =
                                        !proppant.proppantName ||
                                        !proppant.totalVolume ||
                                        !proppant.volumePerBox ||
                                        !proppant.proppantId;
                                } else if (!proppant.pumpScheduleId) {
                                    rowError = false;
                                }
                            } else {
                                rowError = true;
                            }
                            if (rowError) {
                                pageDisabled = true;
                            }
                        });
                    } else {
                        pageDisabled = true;
                    }
                    const oldWellhead = pumpScheduleSummaryList.find(oldWellhead => oldWellhead.id === item.id);
                    if (oldWellhead !== undefined) {
                        if (_.isEqual(oldWellhead, item)) {
                            pageDisabled = true;
                        }
                    }
                } else {
                    pageDisabled = true;
                }
            });
        } else {
            pageDisabled = true;
        }

        return pageDisabled;
    };
    return (
        <React.Fragment>
            <CustomButton
                isBordered={false}
                onClick={() => {
                    preparePumpScheduleEdits(pumpScheduleSummaryList, toBePosted);
                    setShow(true);
                }}
                title="Save"
                isDisabled={isDisabled()}
            />
            {
                <Modal show={show} onHide={handleClose} scrollable size="lg">
                    <Modal.Header className="ConfirmEditPumpSchedule-Header">
                        <Modal.Title className="ConfirmEditPumpSchedule-Header-Container">
                            <ModalHeaderTitle
                                title={'Edit Pump Schedule Confirmation Summary'}
                                onCancelClick={() => {}}
                            ></ModalHeaderTitle>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="ConfirmEditPumpSchedule-Body">
                        {getEditComponents(wellheadEditMap)}
                        {isLoading && (
                            <div className="ConfirmEditPumpSchedule-Loading-Container">
                                <Loading />
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="ConfirmEditPumpSchedule-Footer">
                        <CustomButton title="Cancel" isBordered onClick={handleClose}></CustomButton>
                        <CustomButton
                            title="Continue"
                            isDisabled={isSaveDisabled}
                            onClick={() => {
                                setIsSaveDisabled(true);
                                handleContinue();
                            }}
                        ></CustomButton>
                    </Modal.Footer>
                </Modal>
            }
        </React.Fragment>
    );
};

export default ConfirmEditPumpSchedule;

